import { RowNode } from 'ag-grid-community'
import { isAfter, isBefore, isSameDay, startOfDay } from 'date-fns'
import comparator from 'ramda/src/comparator'
import isNil from 'ramda/src/isNil'
import path from 'ramda/src/path'
import split from 'ramda/src/split'
import ramdaUseWith from 'ramda/src/useWith'
import {
  Timestamp,
  getDateFromTimeStamp
} from '../../../packages/utils/date-utils'

type ComparatorResult = -1 | 0 | 1
type Comparator<T = Record<string, unknown>> = (a: T, b: T) => ComparatorResult
type GuardResult = ComparatorResult | null
type GuardFunction<T = Record<string, unknown>> = (a: T, b: T) => GuardResult

const dotPath = ramdaUseWith(path, [split('.')])
const ALLOWED_VALUES: readonly ComparatorResult[] = [-1, 0, 1] as const

const guard = <T = Record<string, unknown>>(
  field: string
): GuardFunction<T> => {
  return (a: T, b: T): GuardResult => {
    const valA = dotPath(field)(a)
    const valB = dotPath(field)(b)
    const aIsNil = isNil(valA) || Number.isNaN(valA as any)
    const bIsNil = isNil(valB) || Number.isNaN(valB as any)
    if (aIsNil) {
      return bIsNil ? 0 : 1
    }
    if (bIsNil) {
      return -1
    }
    return null
  }
}

export const nestedLocaleComparator =
  (field: string) =>
  (_: unknown, __: unknown, nodeA?: RowNode, nodeB?: RowNode): number => {
    const valA = dotPath(field)(nodeA?.data) ?? ''
    const valB = dotPath(field)(nodeB?.data) ?? ''
    return valA.localeCompare(valB, 'en', { sensitivity: 'base' })
  }

export const dateComparator = (field = 'date', asc = false): Comparator => {
  return (a, b): ComparatorResult => {
    const result = guard(field)(a, b)
    if (ALLOWED_VALUES.includes(result as ComparatorResult)) {
      return result as ComparatorResult
    }
    const aProp = dotPath(field)(a) as Timestamp | undefined
    const bProp = dotPath(field)(b) as Timestamp | undefined

    if (!aProp || !bProp) {
      return 0
    }

    return (comparator((q, t) => isSameDay(t, q) || isAfter(t, q))(
      getDateFromTimeStamp(aProp),
      getDateFromTimeStamp(bProp)
    ) * (asc ? -1 : 1)) as ComparatorResult
  }
}

export const stringComparator = (field = 'name', asc = false): Comparator => {
  return (a, b): ComparatorResult => {
    const result = guard(field)(a, b)
    if (ALLOWED_VALUES.includes(result as ComparatorResult)) {
      return result as ComparatorResult
    }
    const aProp = dotPath(field)(a) as string | undefined
    const bProp = dotPath(field)(b) as string | undefined
    return ((aProp?.localeCompare(bProp ?? '', 'en', {
      sensitivity: 'base',
      numeric: true
    }) ?? 0) * (asc ? 1 : -1)) as ComparatorResult
  }
}

export const dateFilterComparator = (field = 'date'): Comparator => {
  return (a, b): ComparatorResult => {
    const result = guard(field)(a, b)
    if (ALLOWED_VALUES.includes(result as ComparatorResult)) {
      return result as ComparatorResult
    }
    const aProp = dotPath(field)(a) as Timestamp | Date | undefined
    const bProp = dotPath(field)(b) as Timestamp | Date | undefined
    let aDate: Date = aProp as Date
    let bDate: Date = bProp as Date

    if (aProp && typeof aProp === 'object' && 'seconds' in aProp) {
      aDate = startOfDay(getDateFromTimeStamp(aProp as Timestamp))
    }
    if (bProp && typeof bProp === 'object' && 'seconds' in bProp) {
      bDate = startOfDay(getDateFromTimeStamp(bProp as Timestamp))
    }

    if (isBefore(bDate, aDate)) {
      return -1
    } else if (isAfter(bDate, aDate)) {
      return 1
    } else {
      return 0
    }
  }
}
